import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../common';
import { CoverTextElement, CoverSettings, BookType } from '../../types';

interface CoverEditorFullProps {
  bookId: string;
  originalImageUrl: string;
  title: string;
  subtitle?: string | null;
  authorName: string;
  bookType: BookType;
  savedSettings?: CoverSettings | null;
  onSave: (imageData: string, settings: CoverSettings) => void;
  onCancel: () => void;
  onRemove?: () => void;
  isSaving?: boolean;
  isRemoving?: boolean;
  error?: string | null;
}

const PRESET_COLORS = [
  '#FFFFFF', // White
  '#000000', // Black
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#10B981', // Green
  '#8B5CF6', // Purple
  '#EC4899', // Pink
];

const BACKGROUND_PRESET_COLORS = [
  '#000000', // Black
  '#FFFFFF', // White
  '#1F2937', // Gray-800
  '#7C3AED', // Violet
  '#DC2626', // Red
  '#2563EB', // Blue
  '#059669', // Green
  '#D97706', // Amber
];

export default function CoverEditorFull({
  originalImageUrl,
  title,
  subtitle,
  authorName,
  bookType,
  savedSettings,
  onSave,
  onCancel,
  onRemove,
  isSaving = false,
  isRemoving = false,
  error,
}: CoverEditorFullProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 512, height: 768 });

  // Initialize text elements from saved settings or defaults
  const getDefaultTextElements = (width: number, height: number): CoverTextElement[] => {
    const titleText = title || t('books.untitledBook');
    const authorText = authorName || t('books.unknownAuthor');

    if (savedSettings?.textElements && savedSettings.textElements.length > 0) {
      // Update text content with current title/author but keep positions and styles
      return savedSettings.textElements.map((el) => ({
        ...el,
        text:
          el.id === 'title'
            ? titleText
            : el.id === 'author'
              ? authorText
              : el.id === 'subtitle'
                ? subtitle || el.text
                : el.text,
        backgroundColor: el.backgroundColor ?? '#000000',
        backgroundOpacity: el.backgroundOpacity ?? 0.5,
      }));
    }

    const elements: CoverTextElement[] = [
      {
        id: 'title',
        text: titleText,
        x: width / 2,
        y: 100,
        color: '#FFFFFF',
        fontSize: 48,
        fontWeight: 'bold',
        backgroundColor: '#000000',
        backgroundOpacity: 0.5,
      },
    ];

    // Add subtitle for non-fiction books
    if (bookType?.toLowerCase() === 'non_fiction' && subtitle) {
      elements.push({
        id: 'subtitle',
        text: subtitle,
        x: width / 2,
        y: 180,
        color: '#FFFFFF',
        fontSize: 24,
        fontWeight: 'normal',
        backgroundColor: '#000000',
        backgroundOpacity: 0.5,
      });
    }

    elements.push({
      id: 'author',
      text: authorText,
      x: width / 2,
      y: height - 80,
      color: '#FFFFFF',
      fontSize: 24,
      fontWeight: 'normal',
      backgroundColor: '#000000',
      backgroundOpacity: 0.5,
    });

    return elements;
  };

  const [textElements, setTextElements] = useState<CoverTextElement[]>(() =>
    getDefaultTextElements(512, 768)
  );

  const [selectedElement, setSelectedElement] = useState<string | null>('title');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [nextCustomId, setNextCustomId] = useState(1);

  // Update text elements when title/author/subtitle props change
  useEffect(() => {
    const untitledPlaceholder = t('books.untitledBook');
    const unknownAuthorPlaceholder = t('books.unknownAuthor');

    setTextElements((prev) =>
      prev.map((el) => {
        if (el.id === 'title') {
          if ((el.text === untitledPlaceholder || el.text === '') && title) {
            return { ...el, text: title };
          }
        }
        if (el.id === 'author') {
          if ((el.text === unknownAuthorPlaceholder || el.text === '') && authorName) {
            return { ...el, text: authorName };
          }
        }
        if (el.id === 'subtitle' && subtitle) {
          return { ...el, text: subtitle };
        }
        return el;
      })
    );
  }, [title, authorName, subtitle, t]);

  // Load the original image and set canvas dimensions
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      setCanvasDimensions({ width, height });
      setLoadedImage(img);

      // Update text positions if no saved settings
      if (!savedSettings?.textElements) {
        setTextElements((prev) =>
          prev.map((el) => {
            if (el.id === 'title') {
              return { ...el, x: width / 2, y: Math.min(100, height * 0.13) };
            }
            if (el.id === 'subtitle') {
              return { ...el, x: width / 2, y: Math.min(180, height * 0.25) };
            }
            if (el.id === 'author') {
              return { ...el, x: width / 2, y: height - Math.min(80, height * 0.1) };
            }
            return el;
          })
        );
      }
    };
    img.src = originalImageUrl;
  }, [originalImageUrl, savedSettings?.textElements]);

  // Text wrapping helper - handles both manual line breaks and word wrapping
  const wrapText = useCallback((ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const lines: string[] = [];
    const paragraphs = text.split('\n');

    paragraphs.forEach((paragraph) => {
      if (paragraph === '') {
        lines.push('');
        return;
      }

      const words = paragraph.split(' ');
      let currentLine = '';

      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }
    });

    return lines;
  }, []);

  // Draw canvas whenever elements change
  useEffect(() => {
    if (!canvasRef.current || !loadedImage) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasDimensions.width, canvasDimensions.height);
    ctx.drawImage(loadedImage, 0, 0, canvasDimensions.width, canvasDimensions.height);

    textElements.forEach((element) => {
      ctx.save();

      ctx.font = `${element.fontWeight} ${element.fontSize}px "Merriweather", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const lines = wrapText(ctx, element.text, canvasDimensions.width - 40);
      const lineHeight = element.fontSize * 1.3;
      const totalHeight = lines.length * lineHeight;

      const padding = 12;
      const bgColor = element.backgroundColor || '#000000';
      const bgOpacity = element.backgroundOpacity ?? 0.5;

      lines.forEach((line, index) => {
        const metrics = ctx.measureText(line);
        const textWidth = metrics.width;
        const yPos = element.y - totalHeight / 2 + index * lineHeight + lineHeight / 2;

        const r = parseInt(bgColor.slice(1, 3), 16);
        const g = parseInt(bgColor.slice(3, 5), 16);
        const b = parseInt(bgColor.slice(5, 7), 16);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${bgOpacity})`;

        ctx.fillRect(
          element.x - textWidth / 2 - padding,
          yPos - element.fontSize / 2 - padding / 2,
          textWidth + padding * 2,
          element.fontSize + padding
        );
      });

      ctx.fillStyle = element.color;
      lines.forEach((line, index) => {
        const yPos = element.y - totalHeight / 2 + index * lineHeight + lineHeight / 2;
        ctx.fillText(line, element.x, yPos);
      });

      if (element.id === selectedElement) {
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);

        const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
        ctx.strokeRect(
          element.x - maxWidth / 2 - padding - 4,
          element.y - totalHeight / 2 - padding - 4,
          maxWidth + (padding + 4) * 2,
          totalHeight + (padding + 4) * 2
        );
      }

      ctx.restore();
    });
  }, [loadedImage, textElements, selectedElement, canvasDimensions, wrapText]);

  const getCanvasCoords = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current) return { x: 0, y: 0 };

      const rect = containerRef.current.getBoundingClientRect();
      const scaleX = canvasDimensions.width / rect.width;
      const scaleY = canvasDimensions.height / rect.height;

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },
    [canvasDimensions]
  );

  const findElementAt = useCallback(
    (x: number, y: number): CoverTextElement | null => {
      if (!canvasRef.current) return null;

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return null;

      for (let i = textElements.length - 1; i >= 0; i--) {
        const element = textElements[i];
        ctx.font = `${element.fontWeight} ${element.fontSize}px "Merriweather", Georgia, serif`;

        const lines = wrapText(ctx, element.text, canvasDimensions.width - 40);
        const lineHeight = element.fontSize * 1.3;
        const totalHeight = lines.length * lineHeight;
        const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));

        const padding = 16;
        const hitBox = {
          left: element.x - maxWidth / 2 - padding,
          right: element.x + maxWidth / 2 + padding,
          top: element.y - totalHeight / 2 - padding,
          bottom: element.y + totalHeight / 2 + padding,
        };

        if (x >= hitBox.left && x <= hitBox.right && y >= hitBox.top && y <= hitBox.bottom) {
          return element;
        }
      }

      return null;
    },
    [textElements, canvasDimensions]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      const element = findElementAt(coords.x, coords.y);

      if (element) {
        setSelectedElement(element.id);
        setIsDragging(true);
        setDragOffset({
          x: coords.x - element.x,
          y: coords.y - element.y,
        });
      } else {
        setSelectedElement(null);
      }
    },
    [getCanvasCoords, findElementAt]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !selectedElement) return;

      const coords = getCanvasCoords(e.clientX, e.clientY);

      setTextElements((prev) =>
        prev.map((el) =>
          el.id === selectedElement
            ? {
                ...el,
                x: Math.max(50, Math.min(canvasDimensions.width - 50, coords.x - dragOffset.x)),
                y: Math.max(50, Math.min(canvasDimensions.height - 50, coords.y - dragOffset.y)),
              }
            : el
        )
      );
    },
    [isDragging, selectedElement, getCanvasCoords, dragOffset, canvasDimensions]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      const coords = getCanvasCoords(touch.clientX, touch.clientY);
      const element = findElementAt(coords.x, coords.y);

      if (element) {
        setSelectedElement(element.id);
        setIsDragging(true);
        setDragOffset({
          x: coords.x - element.x,
          y: coords.y - element.y,
        });
      }
    },
    [getCanvasCoords, findElementAt]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || !selectedElement || e.touches.length !== 1) return;
      e.preventDefault();

      const touch = e.touches[0];
      const coords = getCanvasCoords(touch.clientX, touch.clientY);

      setTextElements((prev) =>
        prev.map((el) =>
          el.id === selectedElement
            ? {
                ...el,
                x: Math.max(50, Math.min(canvasDimensions.width - 50, coords.x - dragOffset.x)),
                y: Math.max(50, Math.min(canvasDimensions.height - 50, coords.y - dragOffset.y)),
              }
            : el
        )
      );
    },
    [isDragging, selectedElement, getCanvasCoords, dragOffset, canvasDimensions]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleAddTextElement = () => {
    const newElement: CoverTextElement = {
      id: `custom-${nextCustomId}`,
      text: t('books.newTextElement'),
      x: canvasDimensions.width / 2,
      y: canvasDimensions.height / 2,
      color: '#FFFFFF',
      fontSize: 32,
      fontWeight: 'normal',
      backgroundColor: '#000000',
      backgroundOpacity: 0.5,
    };
    setTextElements((prev) => [...prev, newElement]);
    setSelectedElement(newElement.id);
    setNextCustomId((prev) => prev + 1);
  };

  const handleAddSubtitleElement = () => {
    // Check if subtitle already exists
    if (textElements.some((el) => el.id === 'subtitle')) return;

    const titleEl = textElements.find((el) => el.id === 'title');
    const yPos = titleEl ? titleEl.y + 80 : 180;

    const newElement: CoverTextElement = {
      id: 'subtitle',
      text: subtitle || t('books.enterSubtitle'),
      x: canvasDimensions.width / 2,
      y: yPos,
      color: '#FFFFFF',
      fontSize: 24,
      fontWeight: 'normal',
      backgroundColor: '#000000',
      backgroundOpacity: 0.5,
    };
    setTextElements((prev) => {
      // Insert after title if it exists
      const titleIndex = prev.findIndex((el) => el.id === 'title');
      if (titleIndex >= 0) {
        const newArr = [...prev];
        newArr.splice(titleIndex + 1, 0, newElement);
        return newArr;
      }
      return [...prev, newElement];
    });
    setSelectedElement(newElement.id);
  };

  const handleDeleteElement = () => {
    if (!selectedElement || selectedElement === 'title' || selectedElement === 'author') return;

    setTextElements((prev) => prev.filter((el) => el.id !== selectedElement));
    setSelectedElement('title');
  };

  const handleTextChange = (text: string) => {
    if (!selectedElement) return;
    setTextElements((prev) => prev.map((el) => (el.id === selectedElement ? { ...el, text } : el)));
  };

  const handleColorChange = (color: string) => {
    if (!selectedElement) return;
    setTextElements((prev) =>
      prev.map((el) => (el.id === selectedElement ? { ...el, color } : el))
    );
  };

  const handleBackgroundColorChange = (backgroundColor: string) => {
    if (!selectedElement) return;
    setTextElements((prev) =>
      prev.map((el) => (el.id === selectedElement ? { ...el, backgroundColor } : el))
    );
  };

  const handleBackgroundOpacityChange = (backgroundOpacity: number) => {
    if (!selectedElement) return;
    setTextElements((prev) =>
      prev.map((el) => (el.id === selectedElement ? { ...el, backgroundOpacity } : el))
    );
  };

  const handleFontSizeChange = (delta: number) => {
    if (!selectedElement) return;
    setTextElements((prev) =>
      prev.map((el) =>
        el.id === selectedElement
          ? { ...el, fontSize: Math.max(12, Math.min(96, el.fontSize + delta)) }
          : el
      )
    );
  };

  const handlePositionXChange = (x: number) => {
    if (!selectedElement) return;
    setTextElements((prev) =>
      prev.map((el) =>
        el.id === selectedElement
          ? { ...el, x: Math.max(0, Math.min(canvasDimensions.width, x)) }
          : el
      )
    );
  };

  const handlePositionYChange = (y: number) => {
    if (!selectedElement) return;
    setTextElements((prev) =>
      prev.map((el) =>
        el.id === selectedElement
          ? { ...el, y: Math.max(0, Math.min(canvasDimensions.height, y)) }
          : el
      )
    );
  };

  const handleSave = () => {
    if (!canvasRef.current || !loadedImage) return;

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasDimensions.width;
    exportCanvas.height = canvasDimensions.height;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(loadedImage, 0, 0, canvasDimensions.width, canvasDimensions.height);

    textElements.forEach((element) => {
      ctx.save();
      ctx.font = `${element.fontWeight} ${element.fontSize}px "Merriweather", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const lines = wrapText(ctx, element.text, canvasDimensions.width - 40);
      const lineHeight = element.fontSize * 1.3;
      const totalHeight = lines.length * lineHeight;
      const padding = 12;

      const bgColor = element.backgroundColor || '#000000';
      const bgOpacity = element.backgroundOpacity ?? 0.5;

      lines.forEach((line, index) => {
        const metrics = ctx.measureText(line);
        const textWidth = metrics.width;
        const yPos = element.y - totalHeight / 2 + index * lineHeight + lineHeight / 2;

        const r = parseInt(bgColor.slice(1, 3), 16);
        const g = parseInt(bgColor.slice(3, 5), 16);
        const b = parseInt(bgColor.slice(5, 7), 16);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${bgOpacity})`;

        ctx.fillRect(
          element.x - textWidth / 2 - padding,
          yPos - element.fontSize / 2 - padding / 2,
          textWidth + padding * 2,
          element.fontSize + padding
        );
      });

      ctx.fillStyle = element.color;
      lines.forEach((line, index) => {
        const yPos = element.y - totalHeight / 2 + index * lineHeight + lineHeight / 2;
        ctx.fillText(line, element.x, yPos);
      });

      ctx.restore();
    });

    const dataUrl = exportCanvas.toDataURL('image/png');

    const settings: CoverSettings = {
      textElements: textElements,
    };

    onSave(dataUrl, settings);
  };

  const selectedEl = textElements.find((el) => el.id === selectedElement);
  const isCustomElement =
    selectedElement && selectedElement !== 'title' && selectedElement !== 'author';
  const hasSubtitle = textElements.some((el) => el.id === 'subtitle');

  const getElementLabel = (el: CoverTextElement) => {
    if (el.id === 'title') return t('books.coverTitle');
    if (el.id === 'author') return t('books.coverAuthor');
    if (el.id === 'subtitle') return t('books.subtitle');
    return el.text.substring(0, 10) + (el.text.length > 10 ? '...' : '');
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('books.editCover')}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={!loadedImage}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      {/* Error display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="max-w-7xl mx-auto px-4 py-2"
          >
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Canvas area */}
          <div className="flex-1 flex justify-center">
            <div
              ref={containerRef}
              className="relative cursor-move select-none bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden"
              style={{
                width: '100%',
                maxWidth: '450px',
                aspectRatio: `${canvasDimensions.width}/${canvasDimensions.height}`,
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <canvas
                ref={canvasRef}
                width={canvasDimensions.width}
                height={canvasDimensions.height}
                className="w-full h-auto"
              />
              {!loadedImage && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 animate-spin text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Controls panel */}
          <div className="w-full lg:w-80 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 space-y-5">
            {/* Element selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('books.selectElement')}
              </label>
              <div className="flex flex-wrap gap-2">
                {textElements.map((el) => (
                  <button
                    key={el.id}
                    onClick={() => setSelectedElement(el.id)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      selectedElement === el.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {getElementLabel(el)}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleAddTextElement}
                  className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t('books.addTextElement')}
                </button>
                {!hasSubtitle && (
                  <button
                    onClick={handleAddSubtitleElement}
                    className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {t('books.addSubtitle')}
                  </button>
                )}
              </div>
            </div>

            {/* Controls for selected element */}
            {selectedEl && (
              <>
                {/* Text input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('books.editText')}
                  </label>
                  <textarea
                    value={selectedEl.text}
                    onChange={(e) => handleTextChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    rows={2}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('books.textEditHint')}
                  </p>
                </div>

                {/* Position controls */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('books.position')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        X
                      </label>
                      <input
                        type="number"
                        value={Math.round(selectedEl.x)}
                        onChange={(e) => handlePositionXChange(parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        min={0}
                        max={canvasDimensions.width}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Y
                      </label>
                      <input
                        type="number"
                        value={Math.round(selectedEl.y)}
                        onChange={(e) => handlePositionYChange(parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        min={0}
                        max={canvasDimensions.height}
                      />
                    </div>
                  </div>
                </div>

                {/* Text Color picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('books.textColor')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleColorChange(color)}
                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                          selectedEl.color === color
                            ? 'border-primary-500 ring-2 ring-primary-300'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={selectedEl.color}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-full h-8 rounded cursor-pointer mt-2"
                  />
                </div>

                {/* Background Color picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('books.backgroundColor')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {BACKGROUND_PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleBackgroundColorChange(color)}
                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                          (selectedEl.backgroundColor || '#000000') === color
                            ? 'border-primary-500 ring-2 ring-primary-300'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={selectedEl.backgroundColor || '#000000'}
                    onChange={(e) => handleBackgroundColorChange(e.target.value)}
                    className="w-full h-8 rounded cursor-pointer mt-2"
                  />
                </div>

                {/* Background Opacity slider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('books.backgroundOpacity')} (
                    {Math.round((selectedEl.backgroundOpacity ?? 0.5) * 100)}%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={(selectedEl.backgroundOpacity ?? 0.5) * 100}
                    onChange={(e) => handleBackgroundOpacityChange(parseInt(e.target.value) / 100)}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{t('books.transparent')}</span>
                    <span>{t('books.opaque')}</span>
                  </div>
                </div>

                {/* Font size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('books.fontSize')}
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleFontSizeChange(-4)}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    </button>
                    <span className="flex-1 text-center font-mono text-sm">
                      {selectedEl.fontSize}px
                    </span>
                    <button
                      onClick={() => handleFontSizeChange(4)}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Delete button for custom elements */}
                {isCustomElement && (
                  <button
                    onClick={handleDeleteElement}
                    className="w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    {t('books.deleteElement')}
                  </button>
                )}
              </>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">{t('books.coverEditorHint')}</p>

            {/* Remove Cover button */}
            {onRemove && (
              <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="ghost"
                  onClick={onRemove}
                  disabled={isSaving || isRemoving}
                  isLoading={isRemoving}
                  className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  {t('books.removeCover')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
